import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { themeCssVars } from "../../utils/crossroadsTheme";
import "./crossroads.css";

/**
 * Themed modal used for outcomes, "Did You Know?", How to Play and live results. Rendered into
 * <body> so it is centred on the whole screen and sits above the player header; `theme` carries
 * the colours over (CSS variables don't follow a portal).
 */
export function CrossroadsPopup({ open, tone = "neutral", eyebrow, title, children, actions, backgroundUrl, onClose, labelledBy, theme }) {
  const cardRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement;
    // Move focus into the dialog so keyboard users land on its actions.
    const t = window.setTimeout(() => {
      const btn = cardRef.current?.querySelector("button");
      (btn || cardRef.current)?.focus();
    }, 50);
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  const id = labelledBy || "cr-popup-title";
  return createPortal(
    <div className="cr-root cr-popup-backdrop" role="presentation" style={theme ? themeCssVars(theme) : undefined}>
      <div
        ref={cardRef}
        className={`cr-popup cr-popup--${tone}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        tabIndex={-1}
        style={backgroundUrl ? { backgroundImage: `url("${backgroundUrl}")` } : undefined}
      >
        <div className="cr-popup-inner">
          {eyebrow ? <div className="cr-popup-eyebrow">{eyebrow}</div> : null}
          <h2 id={id} className="cr-popup-title">
            {title}
          </h2>
          <div className="cr-popup-body">{children}</div>
          {actions ? <div className="cr-popup-actions">{actions}</div> : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function HowToPlayPopup({ open, onClose, theme }) {
  return (
    <CrossroadsPopup
      open={open}
      onClose={onClose}
      theme={theme}
      title="How to Play"
      labelledBy="cr-howto-title"
      backgroundUrl={theme.images.popup}
      actions={
        <button type="button" className="cr-btn" onClick={onClose}>
          Got it
        </button>
      }
    >
      <ol className="cr-howto-list">
        {theme.rules.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ol>
    </CrossroadsPopup>
  );
}

/** "Did You Know?" content for one crossroads (PRD 3.7). */
export function LearningContent({ question }) {
  return question.more_info ? <p className="cr-learning-text">{question.more_info}</p> : null;
}

export function hasLearningContent(question) {
  return Boolean(question?.more_info);
}

const FEEDBACK_OPTIONS = [
  { value: "Amazing", emoji: "🤩" },
  { value: "Loved it", emoji: "😍" },
  { value: "It was fun", emoji: "😊" },
  { value: "It was okay", emoji: "👍" },
];
const CONFETTI_MS = 2000;

function confettiPieces() {
  const ts = Date.now();
  return Array.from({ length: 64 }, (_, i) => ({
    key: `${ts}-${i}`,
    x: Math.random() * 92 + 4,
    y: Math.random() * 85 + 5,
    dx: (Math.random() - 0.5) * 520,
    dy: (Math.random() - 0.5) * 600 + Math.random() * 120,
    rot: Math.random() * 1080 - 540,
    delay: Math.random() * 0.45,
    size: 36 + Math.floor(Math.random() * 44),
  }));
}

/**
 * After the completion popup: tap an emoji to say how it went. The choice is saved, that emoji
 * bursts across the screen, and the player carries on to the leaderboard. `onSubmit(value)`
 * returns a promise; `onDone` leaves (after a choice, or straight away with Skip).
 */
export function FeedbackPopup({ open, theme, onSubmit, onDone }) {
  const [saved, setSaved] = useState("");
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");
  const [burst, setBurst] = useState(null); // { emoji, pieces }

  useEffect(() => {
    if (!burst) return undefined;
    const t = window.setTimeout(() => setBurst(null), CONFETTI_MS);
    return () => window.clearTimeout(t);
  }, [burst]);

  const choose = async (opt) => {
    if (saved || saving) return;
    setSaving(opt.value);
    setError("");
    try {
      await onSubmit(opt.value);
      setSaved(opt.value);
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (!reduceMotion) setBurst({ emoji: opt.emoji, pieces: confettiPieces() });
    } catch (err) {
      setError(err?.message || "Couldn't save your feedback — please try again.");
    } finally {
      setSaving("");
    }
  };

  const leaderboard = theme?.leaderboardEnabled !== false;
  return (
    <>
      <CrossroadsPopup
        open={open}
        theme={theme}
        tone="neutral"
        eyebrow="Thank you!"
        title="How was your experience?"
        labelledBy="cr-feedback-title"
        backgroundUrl={theme?.images?.popup}
        actions={
          saved ? (
            <button type="button" className="cr-btn" onClick={onDone}>
              {leaderboard ? (
                <>
                  <i className="fa-solid fa-trophy" aria-hidden /> Leaderboard
                </>
              ) : (
                <>
                  Next <i className="fa-solid fa-arrow-right" aria-hidden />
                </>
              )}
            </button>
          ) : (
            <button type="button" className="cr-btn-ghost" onClick={onDone} disabled={Boolean(saving)}>
              Skip
            </button>
          )
        }
      >
        <p className="cr-fb-hint">Tap an emoji to share your thoughts</p>
        <div className="cr-fb-grid">
          {FEEDBACK_OPTIONS.map((opt) => {
            const selected = saved === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`cr-fb-card${selected ? " is-selected" : ""}${saved && !selected ? " is-faded" : ""}`}
                onClick={() => choose(opt)}
                disabled={Boolean(saved || saving)}
                aria-pressed={selected}
              >
                <span className="cr-fb-emoji" aria-hidden>
                  {opt.emoji}
                </span>
                <span className="cr-fb-label">{saving === opt.value ? "Saving…" : opt.value}</span>
              </button>
            );
          })}
        </div>
        {error ? (
          <p className="cr-feedback-error" role="alert">
            {error}
          </p>
        ) : null}
        <p className="cr-fb-hint cr-fb-hint--small">
          {saved ? "Thanks — your feedback helps us improve!" : "Your feedback helps us improve!"}
        </p>
      </CrossroadsPopup>
      {open && burst
        ? createPortal(
            <div className="cr-confetti" aria-hidden="true">
              {burst.pieces.map((pc) => (
                <span
                  key={pc.key}
                  className="cr-confetti-emoji"
                  style={{
                    left: `${pc.x}%`,
                    top: `${pc.y}%`,
                    fontSize: pc.size,
                    "--cf-dx": `${pc.dx}px`,
                    "--cf-dy": `${pc.dy}px`,
                    "--cf-rot": `${pc.rot}deg`,
                    animationDelay: `${pc.delay}s`,
                  }}
                >
                  {burst.emoji}
                </span>
              ))}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export function StatTile({ label, value }) {
  return (
    <div className="cr-stat">
      <div className="cr-stat-value">{value}</div>
      <div className="cr-stat-label">{label}</div>
    </div>
  );
}

export function Spinner({ label = "Loading…" }) {
  return (
    <div className="cr-center" aria-busy="true">
      <div className="cr-spinner" />
      <span className="cr-sr-only">{label}</span>
    </div>
  );
}
